import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  SystemLockout,
  SystemLockoutCreate,
  SystemLockoutUpdate,
  SystemLockoutApiResponse,
  SystemLockoutPagedResponse,
  SystemLockoutListResponse,
  SystemLockoutStatus
} from '../models/SystemLockout';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SystemLockoutService {
  private baseUrl: string;
  private authCode = '-Pf-epql89Yr1U7x-zQWCtok_i1LJS-1wAEQHILHn8DyAzFuVVvuxg==';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {
    this.baseUrl = environment.microservices?.auth ?? environment.apiUrl;
  }

  private buildUrl(endpoint: string, params?: HttpParams): string {
    let url = `${this.baseUrl}${endpoint}`;
    const isLocalhost = this.baseUrl.includes('localhost');
    if (!isLocalhost) {
      const separator = endpoint.includes('?') ? '&' : '?';
      url += `${separator}code=${this.authCode}`;
    }
    if (params) {
      const paramString = params.toString();
      if (paramString) {
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}${paramString}`;
      }
    }
    return url;
  }

  /**
   * 1. Listar todos os System Lockouts (paginado)
   * GET /api/system-lockout?page=1&pageSize=50
   */
  getAll(options?: PaginationOptions): Observable<SystemLockoutPagedResponse> {
    let params = new HttpParams();
    if (options?.page) {
      params = params.set('page', options.page.toString());
    }
    if (options?.pageSize) {
      params = params.set('pageSize', options.pageSize.toString());
    }

    const url = this.buildUrl('/system-lockout', params);
    return this.http.get<SystemLockoutPagedResponse>(url).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar bloqueios de sistema');
        return throwError(() => err);
      })
    );
  }

  /**
   * 2. Buscar System Lockout por ID
   * GET /api/system-lockout/{id}
   */
  getById(id: number): Observable<SystemLockout> {
    const url = this.buildUrl(`/system-lockout/${id}`);
    return this.http.get<SystemLockoutApiResponse>(url).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Bloqueio de sistema não encontrado');
      }),
      catchError(err => {
        if (err.status !== 404) {
          this.errorService.showError('Erro ao buscar bloqueio de sistema');
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * 3. Listar System Lockouts ativos
   * GET /api/system-lockout/active
   */
  getActive(): Observable<SystemLockout[]> {
    const url = this.buildUrl('/system-lockout/active');
    return this.http.get<SystemLockoutListResponse>(url).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        return [];
      }),
      catchError(err => {
        this.errorService.showError('Erro ao buscar bloqueios ativos');
        return throwError(() => err);
      })
    );
  }

  /**
   * 4. Listar System Lockouts pendentes
   * GET /api/system-lockout/pending
   */
  getPending(): Observable<SystemLockout[]> {
    const url = this.buildUrl('/system-lockout/pending');
    return this.http.get<SystemLockoutListResponse>(url).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        return [];
      }),
      catchError(err => {
        this.errorService.showError('Erro ao buscar bloqueios pendentes');
        return throwError(() => err);
      })
    );
  }

  /**
   * 5. Listar System Lockouts por status
   * GET /api/system-lockout/status/{status}
   */
  getByStatus(status: SystemLockoutStatus): Observable<SystemLockout[]> {
    const url = this.buildUrl(`/system-lockout/status/${status}`);
    return this.http.get<SystemLockoutListResponse>(url).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        return [];
      }),
      catchError(err => {
        this.errorService.showError('Erro ao buscar bloqueios por status');
        return throwError(() => err);
      })
    );
  }

  /**
   * 6. Listar System Lockouts por intervalo de datas
   * GET /api/system-lockout/date-range?startDate=2026-01-01&endDate=2026-12-31
   */
  getByDateRange(startDate: string, endDate: string): Observable<SystemLockout[]> {
    let params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    const url = this.buildUrl('/system-lockout/date-range', params);
    return this.http.get<SystemLockoutListResponse>(url).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        return [];
      }),
      catchError(err => {
        this.errorService.showError('Erro ao buscar bloqueios por período');
        return throwError(() => err);
      })
    );
  }

  /**
   * 7. Criar novo System Lockout
   * POST /api/system-lockout
   */
  create(systemLockout: SystemLockoutCreate): Observable<SystemLockout> {
    const url = this.buildUrl('/system-lockout');
    return this.http.post<SystemLockoutApiResponse>(url, systemLockout).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Erro ao criar bloqueio de sistema');
      }),
      catchError(err => {
        this.errorService.showError(err.error?.message || 'Erro ao criar bloqueio de sistema');
        return throwError(() => err);
      })
    );
  }

  /**
   * 8. Atualizar System Lockout
   * PUT /api/system-lockout/{id}
   */
  update(id: number, systemLockout: SystemLockoutUpdate): Observable<void> {
    const url = this.buildUrl(`/system-lockout/${id}`);
    return this.http.put<SystemLockoutApiResponse>(url, systemLockout).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.message || 'Erro ao atualizar bloqueio de sistema');
        }
      }),
      catchError(err => {
        this.errorService.showError(err.error?.message || 'Erro ao atualizar bloqueio de sistema');
        return throwError(() => err);
      })
    );
  }

  /**
   * 9. Deletar System Lockout (soft delete)
   * DELETE /api/system-lockout/{id}
   */
  delete(id: number): Observable<void> {
    const url = this.buildUrl(`/system-lockout/${id}`);
    return this.http.delete<SystemLockoutApiResponse>(url).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.message || 'Erro ao deletar bloqueio de sistema');
        }
      }),
      catchError(err => {
        this.errorService.showError(err.error?.message || 'Erro ao deletar bloqueio de sistema');
        return throwError(() => err);
      })
    );
  }

  /**
   * 10. Aprovar primeira aprovação
   * POST /api/system-lockout/{id}/approve-first
   */
  approveFirst(id: number): Observable<void> {
    const url = this.buildUrl(`/system-lockout/${id}/approve-first`);
    return this.http.post<SystemLockoutApiResponse>(url, {}).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.message || 'Erro ao realizar primeira aprovação');
        }
      }),
      catchError(err => {
        this.errorService.showError(err.error?.message || 'Erro ao realizar primeira aprovação');
        return throwError(() => err);
      })
    );
  }

  /**
   * 11. Aprovar segunda aprovação
   * POST /api/system-lockout/{id}/approve-second
   */
  approveSecond(id: number): Observable<void> {
    const url = this.buildUrl(`/system-lockout/${id}/approve-second`);
    return this.http.post<SystemLockoutApiResponse>(url, {}).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.message || 'Erro ao realizar segunda aprovação');
        }
      }),
      catchError(err => {
        this.errorService.showError(err.error?.message || 'Erro ao realizar segunda aprovação');
        return throwError(() => err);
      })
    );
  }

  /**
   * 12. Rejeitar System Lockout
   * POST /api/system-lockout/{id}/reject
   */
  reject(id: number): Observable<void> {
    const url = this.buildUrl(`/system-lockout/${id}/reject`);
    return this.http.post<SystemLockoutApiResponse>(url, {}).pipe(
      map(response => {
        if (!response.success) {
          throw new Error(response.message || 'Erro ao rejeitar bloqueio de sistema');
        }
      }),
      catchError(err => {
        this.errorService.showError(err.error?.message || 'Erro ao rejeitar bloqueio de sistema');
        return throwError(() => err);
      })
    );
  }
}
